import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { GlobalService } from '@home/src/app/core/services/global/global.service';
import { DealersDropDown, GroupMember } from '@home/src/app/Interface/Dealer';
import { Subject, takeUntil } from 'rxjs';
import { DropdownComponent } from '@app/shared/components/dropdown/dropdown/dropdown.component';
import { setGroupMemberCode, setRole } from '@core/interceptors/fetch-client';
import { AuthenticationService } from '@home/src/app/core/services/Auth/authservice';
import mockClaims from '@src/app/mockdata/mockclaimsdata.json';
@Component({
  selector: 'app-dealer-select',
  standalone: true,
  imports: [DropdownComponent],
  templateUrl: './dealer-select.component.html',
  styleUrl: './dealer-select.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealerSelectComponent implements OnDestroy, OnInit {
  private readonly unsubscribe$ = new Subject<void>();
  public GroupName: string | null = null;
  groupId: number | null = null;
  allDealers: DealersDropDown[] | null = null;
  orgDetails: GroupMember[] | null = null;
  selectedDealer: GroupMember | null = null;
  isDropdownOpen = false; // Track dropdown state
  isGroupDropdownDisabled = false;
  isDealerDropdownDisabled = false;
  selectedRole: string | null = null;
  groupList: {
    positionCode: string;
    dealerCode?: string;
    groupName: string;
    groupMemberCode: string;
  }[] = [];
  selectedGroup: {
    positionCode: string;
    dealerCode?: string;
    groupName: string;
    groupMemberCode: string;
  } | null = null;

  @ViewChild('dropdownRef') dropdownRef!: DropdownComponent;
  constructor(
    private globalService: GlobalService,
    private cdr: ChangeDetectorRef,
    private eRef: ElementRef,
    private authTokenService: AuthenticationService,
  ) {}

  ngOnInit() {
    this.globalService.groupName$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((val) => {
        this.GroupName = val;
        this.cdr.markForCheck();
      });
    this.globalService.orgDetails$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((val) => {
        this.orgDetails = val;
        this.cdr.markForCheck();
      });
    this.globalService.selectedDealer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((val) => {
        if (val) {
          this.selectedDealer = val;
          this.cdr.markForCheck();
        }
      });
    this.globalService.selectedRole$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((role) => {
        this.selectedRole = role;
        this.isDealerDropdownDisabled = role !== 'CONSULT' && role !== 'STAFF';
        // Group dropdown always enabled
        this.isGroupDropdownDisabled = false;
        this.cdr.markForCheck();
      });
    const committees = this.authTokenService.getComitteesData();
    if (committees && Array.isArray(committees)) {
      this.groupList = committees
        .map((item: any) => ({
          positionCode: item.positionCode,
          groupName: item.code,
          groupMemberCode: item.fullcode,
          dealerCode: item.dealerCode,
        }))
        .sort((a, b) => a.groupMemberCode.localeCompare(b.groupMemberCode));
    } else {
      this.groupList = [];
    }

    const savedGroup = sessionStorage.getItem('fincomData');
    if (savedGroup) {
      try {
        const parsedGroup = JSON.parse(savedGroup);
        const foundGroup = this.groupList.find(
          (g) => g.groupMemberCode === parsedGroup.fullcode,
        );

        if (foundGroup) {
          this.selectedGroup = foundGroup;
          sessionStorage.setItem(
            'selectedGroup',
            JSON.stringify(this.selectedGroup),
          );
        } else {
          console.warn('Saved group not found in current list.');
        }
      } catch (err) {
        console.error('Error parsing savedGroup:', err);
      }
    }
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (
      this.isDropdownOpen &&
      !this.eRef.nativeElement.contains(event.target)
    ) {
      this.isDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  onGroupSelect(selectedValue: any) {
    this.selectedGroup = selectedValue;
    const key = 'fincomData';
    const fincompDataStr = sessionStorage.getItem(key);
    try {
      let fincompData: any;
      if (fincompDataStr) {
        fincompData = JSON.parse(fincompDataStr);
      } else {
        fincompData = { ...mockClaims };
      }
      let positionCode = selectedValue?.positionCode?.replace(/\d+$/, '');
      if (positionCode === 'VCP') {
        positionCode = 'DLR';
      }
      fincompData.positionCode = positionCode;
      let trimmed = selectedValue?.groupMemberCode.trim();
      // If last character is 'M', remove only that last character
      if (trimmed.endsWith('M')) {
        trimmed = trimmed.slice(0, -1);
      }
      fincompData.committeeCode = trimmed?.slice(0, 4);
      fincompData.dealerCode = selectedValue?.dealerCode;
      fincompData.fullcode = selectedValue?.groupMemberCode;

      let landingPage = 'Financial-Composite/KPI';
      const role = selectedValue?.positionCode?.toUpperCase?.() || '';
      if (role === 'OFM') {
        landingPage = '/Input-Sheet';
      }
      this.updateClaimsData(fincompData);
      sessionStorage.setItem(key, JSON.stringify(fincompData));
      sessionStorage.setItem('selectedGroup', JSON.stringify(selectedValue));
      window.location.href = landingPage;
    } catch (error) {
      console.error('Error parsing or saving fincompData:', error);
    }
  }

  private updateClaimsData(claimsData: any): void {
    const { committeeCode, positionCode } = claimsData;
    if (!committeeCode) return;
    this.globalService.setSelectedRole(positionCode);
    setGroupMemberCode(committeeCode);
    this.globalService.updateGroupMemberCode(committeeCode);
    setRole(positionCode);
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  async onSelect(selectedValue: GroupMember) {
    const selecteddealerCode = selectedValue?.groupMemberCode.slice(-2);
    this.authTokenService.overWriteClaimsData({
      dealerCode: selecteddealerCode,
    });
    setGroupMemberCode(selectedValue.groupMemberCode);
    this.globalService.updateSelectedDealers(selectedValue);
    this.isDropdownOpen = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.complete();
  }
}
